import {
  EntityAzureGitTagsContent,
  EntityAzurePullRequestsContent,
} from '@backstage-community/plugin-azure-devops';
import { BazaarPage } from '@backstage-community/plugin-bazaar';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {
  configApiRef,
  IdentityApi,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogGraphPage,
  catalogGraphPlugin,
} from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import {
  exportPublicKeyBase64,
  fetchData,
  loadOrCreateKeyPair,
  urls,
} from '@internal/backstage-plugin-dh-dock-shared-common';
import {
  DomainPageAboutCardPage,
  domainPageAboutCardPlugin,
} from '@internal/plugin-domain-page-about-card';
import { SduiPreviewPage } from '@internal/plugin-sdui-preview';
import { CssBaseline, ThemeProvider } from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import React, { useMemo } from 'react';
import { Provider } from 'react-redux';
import { Route } from 'react-router-dom';

import { apis } from './apis';
import { LocalDevelopmentSignInPage } from './auth/LocalDevelopmentSignInPage';
import {
  isDcpDevelopmentAuthUiEnabled,
  isStaticAuthUiEnabled,
} from './auth/localAuth';
import { entityPage } from './components/catalog/EntityPage';
import { FlaggedHomePage } from './components/home/FlaggedHomePage';
import { dhDockHomepage } from './hooks/config';
import { Root } from './components/Root';
import { searchPage } from './components/search/SearchPage';
import { customTheme } from './customTheme';
import { GlobalApiInterceptor } from './GlobalApiInterceptor';
import { CustomCatalogIndexPage } from './pages/CustomCatalog';
import { FeatureFlagsComponent } from './pages/DAP/FeatureFlagsComponent';
import GuardRails from './pages/GuardRails';
import MarkDownPreview from './pages/MarkDownPreview';
import TechRadarHome from './pages/techRadar';
import QuadrantOverview from './pages/techRadar/overview';
import ItemPage from './pages/techRadar/quadrant';
import {
  AadGroupPickerFieldContributorExtension,
  AadGroupPickerFieldExtension,
} from './pages/templates/AadGroupPicker';
import { AzureDevOpsValidator } from './pages/templates/AzureDevopsValidator/extension';
import { OdpNameExtension } from './pages/templates/Odp/extension';
import { OIPFileUpload } from './pages/templates/OIP/extension';
import { DomainSelectExtension } from './pages/templates/SelectList/extension';
import {
  TrackSpaceExtension,
  TrackSpaceUserListExtension,
} from './pages/templates/Trackspace-Integration/extension';
import { CoPilotComplianceCheckExtension } from './pages/templates/LoggedInUser/extension';
import { DcpProductPickerFieldExtension } from './pages/templates/DcpProductPicker';
import { EntityValidationPage } from './pages/YamlValidation';
import { setAdrList } from './redux/slices/adrSlice';
import {
  setGlobalIdentity,
  setLoggedInUser,
  setLoggedInUserEmail,
  setUserToken,
} from './redux/slices/authSlice';
import { setCustomGroupInfo } from './redux/slices/customGroupInfoSlice';
import {
  entraIdGroupProps,
  setEntraIdGroups,
} from './redux/slices/entraIdSlice';
import {
  fetchProjectAsync,
  setAppInitializationFailed,
  setAppInitializationStarted,
  setAppInitializationSucceeded,
} from './redux/slices/globalVariableSlice';
import { fetchHieracyListAsync } from './redux/slices/hierachySlice';
import { fetchOwnersAsync } from './redux/slices/ownerShipSlice';
import { fetchTechRadarAsync } from './redux/slices/techRadarSlice';
import { dhDockStore } from './redux/store';
import {
  GuardrailCompliancePage,
} from '@internal/backstage-backstage-plugin-guardrail-compliance';
import { ProductSecurityPage } from '@internal/backstage-plugin-product-security';

const GuideLines = React.lazy(() => import('./pages/GuideLines'));

const INITIALIZATION_STEP_TIMEOUT_MS = 15000;

async function runInitializationStep<T>(
  label: string,
  task: Promise<T>,
  timeoutMs: number = INITIALIZATION_STEP_TIMEOUT_MS,
): Promise<T> {
  const startedAt = Date.now();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    const result = await Promise.race([task, timeoutPromise]);
    console.info(
      `[app-init] ${label} completed in ${Date.now() - startedAt}ms`,
    );
    return result as T;
  } catch (error) {
    console.error(
      `[app-init] ${label} failed after ${Date.now() - startedAt}ms`,
      error,
    );
    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

async function initializeApplicationData(
  email: string,
  initEncryption: (email: string) => Promise<void>,
) {
  try {
    dhDockStore.dispatch(setAppInitializationStarted());

    const results = await Promise.allSettled([
      runInitializationStep(
        'load entra id groups',
        fetchData(urls.getEntraIdGroupList),
      ),
      runInitializationStep(
        'load custom group info',
        fetchData(urls.getAllCustomGroupInfo),
      ),
      runInitializationStep('load adr list', fetchData(urls.getAllAdrs)),
      runInitializationStep('initialize encryption', initEncryption(email)),
    ]);
    const [entraResult, customResult, adrResult] = results;
    if (entraResult.status === 'fulfilled') {
      dhDockStore.dispatch(
        setEntraIdGroups(entraResult.value as entraIdGroupProps[]),
      );
    }
    if (customResult.status === 'fulfilled') {
      dhDockStore.dispatch(setCustomGroupInfo(customResult.value));
    }
    if (adrResult.status === 'fulfilled') {
      dhDockStore.dispatch(setAdrList(adrResult.value));
    }

    await Promise.allSettled([
      runInitializationStep(
        'fetch owners',
        dhDockStore.dispatch(fetchOwnersAsync()),
      ),
      runInitializationStep(
        'fetch hierarchy',
        dhDockStore.dispatch(fetchHieracyListAsync()),
      ),
      runInitializationStep(
        'fetch tech radar',
        dhDockStore.dispatch(fetchTechRadarAsync()),
      ),
      runInitializationStep(
        'fetch project',
        dhDockStore.dispatch(fetchProjectAsync()),
      ),
    ]);

    dhDockStore.dispatch(setAppInitializationSucceeded());
  } catch (error) {
    console.error('Application initialization failed', error);
    dhDockStore.dispatch(
      setAppInitializationFailed('Application initialization failed'),
    );
  }
}

const app = createApp({
  apis,
  // support authentication via Azure AD
  components: {
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const providerConfig = useMemo(
        () => ({
          id: 'azure-auth-provider',
          title: 'Azure',
          message: 'Sign in using Azure AD',
          apiRef: microsoftAuthApiRef,
        }),
        [],
      );

      const initEncryption = async (
        email: string,
        retries = 3,
      ): Promise<void> => {
        try {
          const kp = await loadOrCreateKeyPair(email);
          const publicKey = await exportPublicKeyBase64(kp.publicKey);

          await fetchData(urls.saveGeneratedKey, {
            key: publicKey,
          });
        } catch (error) {
          console.error('Failed to initialize keypair', error);

          if (retries <= 0) {
            throw error;
          }

          return initEncryption(email, retries - 1);
        }
      };

      const onSignInSuccessHandler = useMemo(
        () => async (identityApi: IdentityApi) => {
          try {
            const userToken = await identityApi.getCredentials();
            const { email, displayName } = await identityApi.getProfileInfo();
            const store = dhDockStore.getState();

            dhDockStore.dispatch(setUserToken(userToken?.token || ''));
            dhDockStore.dispatch(setGlobalIdentity(identityApi));
            dhDockStore.dispatch(setLoggedInUser(displayName));
            dhDockStore.dispatch(setLoggedInUserEmail(email));

            props.onSignInSuccess(identityApi);

            if (
              store.ownersProps.owners.length < 1 &&
              store.globalState.appInitializationStatus === 'idle'
            ) {
              setTimeout(() => {
                void initializeApplicationData(email as string, initEncryption);
              }, 0);
            }
          } catch (error) {
            console.error('Sign-in failed:', error);
          }
        },
        [props.onSignInSuccess, initEncryption],
      );

      if (isStaticAuthUiEnabled(configApi)) {
        return (
          <LocalDevelopmentSignInPage
            onSignInSuccess={onSignInSuccessHandler}
            hostedDevelopment={isDcpDevelopmentAuthUiEnabled(configApi)}
          />
        );
      }

      return (
        <SignInPage
          {...props}
          provider={providerConfig}
          onSignInSuccess={onSignInSuccessHandler}
        />
      );
    },
  },
  themes: [
    {
      id: 'custom-theme',
      title: 'DH Custom Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <ThemeProvider theme={customTheme}>
          <CssBaseline>{children}</CssBaseline>
        </ThemeProvider>
      ),
    },
  ],
  featureFlags: [
    {
      pluginId: '',
      name: dhDockHomepage,
      description: 'Enables the DH Dock homepage plugin',
    },
    {
      pluginId: '',
      name: 'about-card',
      description: 'Enables the custom about card to be displayed',
    },
  ],
  // support custom icons
  // https://backstage.io/docs/getting-started/app-custom-theme#icons
  icons: {
    code: CodeIcon,
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogGraphPlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
    bind(domainPageAboutCardPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <FlaggedHomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogIndexPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name/"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid
          darkConfig={{ theme: 'dark' }}
          lightConfig={{ theme: 'forest' }}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />

    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <AadGroupPickerFieldExtension />
        <AadGroupPickerFieldContributorExtension />
        <AzureDevOpsValidator />
        <OdpNameExtension />
        <TrackSpaceExtension />
        <CoPilotComplianceCheckExtension />
        <TrackSpaceUserListExtension />
        <OIPFileUpload />
        <DomainSelectExtension />
        <DcpProductPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />

    <Route path="/techRadar" element={<TechRadarHome />} />
    <Route
      path="/entity-validation"
      element={<EntityValidationPage from="default" />}
    />
    <Route path="/mark-down-preview" element={<MarkDownPreview />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/guardrails" element={<GuardRails />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />

    <Route path="/domain-page-about" element={<DomainPageAboutCardPage />} />
    <Route path="/sdui-preview" element={<SduiPreviewPage />} />
    <Route path="/dap-feature-flags" element={<FeatureFlagsComponent />} />
    <Route path="/:quadrant/:id" element={<ItemPage />} />
    <Route path="/techRadar/:quadrant" element={<QuadrantOverview />} />
    <Route path="/guidelines" element={<GuideLines from="Home" />} />
    <Route path="/bazaar" element={<BazaarPage title="Working Groups" />} />
    <Route
      path="/EntityAzurePullRequestsContent"
      element={<EntityAzurePullRequestsContent />}
    />

    <Route
      path="/EntityAzureGitTagsContent"
      element={<EntityAzureGitTagsContent />}
    />
    <Route path="/guardrail-compliance" element={<GuardrailCompliancePage />} />
    <Route path="/product-security" element={<ProductSecurityPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <Provider store={dhDockStore}>
      <AlertDisplay />
      <GlobalApiInterceptor />
      <OAuthRequestDialog />
      <SearchContextProvider>
        <AppRouter>
          <Root>{routes}</Root>
        </AppRouter>
      </SearchContextProvider>
    </Provider>
  </>,
);
